import { Monitor, Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTheme, type ThemeMode } from "../../lib/theme";

interface Props {
  // Desktop header uses the compact icon-only trigger; the mobile drawer
  // shows a wider row with labels. One prop so the two sites can't drift.
  variant?: "compact" | "drawer";
}

const OPTIONS: Array<{ mode: ThemeMode; label: string; Icon: typeof Sun }> = [
  { mode: "light", label: "Light", Icon: Sun },
  { mode: "dark", label: "Dark", Icon: Moon },
  { mode: "system", label: "System", Icon: Monitor },
];

export function ThemeToggle({ variant = "compact" }: Props) {
  const { mode, resolved, setMode } = useTheme();
  const ActiveIcon = resolved === "dark" ? Moon : Sun;

  if (variant === "drawer") {
    return (
      <div
        data-testid="drawer-theme-toggle"
        className="flex items-center gap-1 rounded-md bg-[var(--ink-100)] p-1"
      >
        {OPTIONS.map(({ mode: m, label, Icon }) => (
          <button
            key={m}
            type="button"
            data-testid={`drawer-theme-${m}`}
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
              mode === m
                ? "bg-white text-[var(--ink-900)] shadow-sm dark:bg-[var(--ink-950)] dark:text-[var(--ink-0)]"
                : "text-[var(--ink-500)] hover:text-[var(--ink-900)]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="topbar-theme-toggle"
          aria-label="Toggle theme"
          title="Theme"
          className="flex items-center justify-center text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-100)] w-8 h-8 rounded-md transition-colors"
        >
          <ActiveIcon className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {OPTIONS.map(({ mode: m, label, Icon }) => (
          <DropdownMenuItem
            key={m}
            data-testid={`topbar-theme-${m}`}
            onSelect={() => setMode(m)}
            className={mode === m ? "font-semibold" : ""}
          >
            <Icon className="w-4 h-4 text-[var(--ink-500)]" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
